import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reimbursementModStyles } from './ReimbursementMod.styles';
import { Grid, InputAdornment, TextField } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { isNumber } from '../../ModificationsUtils';
import { usePromiseTracker } from 'react-promise-tracker';
import { validDecimal } from 'app/helpers/numberHelper';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { reimbursementDescriptionId, reimbursementCategoryId, ApprovalStatus } from 'app/models/Placement/PlacementMods';
import { useSelector } from 'react-redux';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { selectCurrentModDetails } from 'store/redux-store/placement-mods/selectors';
import * as StatusDefaults from 'app/components/Placement/NextPlacementStatus/StatusDefaults';
import { isMiscellaneousCellPhoneStipend } from './ReimbursementHelper';

export const CapAmount = ({ isExpanded, isModEdit, defaultAmount }: 
  { isExpanded: boolean; isModEdit: boolean; defaultAmount?: number; }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue, formState: { errors }, setError, clearErrors } = useFormContext();
  const { classes } = reimbursementModStyles({ isExpanded });
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });
  const capAmount: number | undefined = useWatch({ name: 'amount' }); 
  const isFirstRender = useRef(true);
  const initialDescriptionRef = useRef<number | null>(null);
  const reimbursementDescriptionValue = useWatch<any>({ name: reimbursementDescriptionId });
  const placementStatus = useSelector(selectPlacementStatus);
  const isActiveStatus = StatusDefaults.isPositiveActiveStatus(placementStatus?.currentPlacementStatus);
  const [isAmountDirty, setAmountDirty] = useState(false);
  const [updateMethod, setUpdateMethod] = useState<'dropdown' | 'textfield'>('dropdown');
  const reimbursementCategoryIdValue: any = useWatch({ name: reimbursementCategoryId });
  const currentModDetails = useSelector(selectCurrentModDetails);
  const isApproved = currentModDetails?.approvalStatusId === ApprovalStatus.Approved;

  useEffect(() => {
    if (!isFirstRender.current && reimbursementDescriptionValue !== null &&
       initialDescriptionRef.current !== reimbursementDescriptionValue) { 
      setAmountDirty(true);
      initialDescriptionRef.current = reimbursementDescriptionValue;
    }
    setUpdateMethod('dropdown');
    isFirstRender.current = false;
  }, [reimbursementDescriptionValue]);

  useEffect(() => {
    if (!isAmountDirty) {
      // On first render use capAmount if it's not null otherwise use default
      if (isMiscellaneousCellPhoneStipend(reimbursementCategoryIdValue, reimbursementDescriptionValue) 
         && isActiveStatus && isModEdit) {
        if (isApproved) { // If the mod is approved, use the default amount
          setValue('amount', defaultAmount, { shouldDirty: true });
          clearErrors('amount');
          clearErrors('initialAmount');
        } else {
          if (capAmount !== null && capAmount !== undefined) {
            setValue('amount', capAmount, { shouldDirty: true });
            clearErrors('amount');
            clearErrors('initialAmount');
          } else {
            setValue('amount', defaultAmount, { shouldDirty: true });
            clearErrors('amount');
            clearErrors('initialAmount');
          }
        }
        isFirstRender.current = false;
      } else if (capAmount !== null && capAmount !== undefined) {
        setValue('amount', capAmount, { shouldDirty: true });
        clearErrors('amount');
        clearErrors('initialAmount');
      } else {
        setValue('amount', defaultAmount, { shouldDirty: true });
        clearErrors('amount');
        clearErrors('initialAmount');
      }
      isFirstRender.current = false;
    } else if (isModEdit) {
      let newAmount = null;
      if (updateMethod === 'dropdown') {
        newAmount = defaultAmount ?? capAmount;
      }
      else {
        newAmount = capAmount;
      }
      setValue('amount', newAmount, { shouldDirty: true });
      clearErrors('amount');
      clearErrors('initialAmount');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAmount, isModEdit, capAmount, isAmountDirty, updateMethod, isApproved, reimbursementCategoryIdValue, 
     reimbursementDescriptionValue, isActiveStatus]);

  return (
    <Grid>
      <Grid item xs={12}>
        <Controller
          name="amount"
          control={control}
          rules={{
            required: isModEdit,
          }}
          render={({ field: { onChange, ...rest } }) => (
            <TextField
              value={capAmount ?? ''}
              className={classes.dollarTextfields}
              InputLabelProps={{
                shrink: (isNumber(capAmount) || capAmount) ?? false,
              }}
              variant="filled"
              label={t(`placement.placementMods.capAmount`)}
              onChange={e => {
                if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                  setUpdateMethod('textfield');
                  setAmountDirty(true);
                  setValue('amount', parseInt(e.target.value), { shouldDirty: true });
                  onChange(e.target.value);
                  clearErrors('amount');
                } else if (!e.target.value) {
                  setError('amount', {});
                }
                clearErrors('initialAmount');
              }}
              InputProps={{
                readOnly: !isModEdit,
                style: {
                  pointerEvents: !isModEdit ? 'none' : 'auto',
                },
                startAdornment: (
                  <>
                    {isNumber(getValues('amount')) && (
                      <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                    )}
                  </>
                ),
                sx: { fontSize: '14px' },
              }}
              disabled={isSaveModApiCalling}
              {...rest}
              error={!!errors?.amount}
              helperText={!isNullOrUndefined(errors?.amount) ? 'Required' : undefined}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
